import { createContext, useContext, useEffect, useMemo, useReducer, useRef } from "react";
// ============================================================================
// Storage Helpers
// ============================================================================
const STORAGE_KEY_ROOT = "Babylon/Accordion";
const InMemoryStorage = new Map();
// eslint-disable-next-line no-console
const Warn = (message) => globalThis.console?.warn?.(message);
const ReadStoredString = (key) => {
    try {
        return globalThis.localStorage?.getItem(key) ?? InMemoryStorage.get(key) ?? "";
    }
    catch {
        return InMemoryStorage.get(key) ?? "";
    }
};
const WriteStoredString = (key, value) => {
    try {
        if (globalThis.localStorage) {
            globalThis.localStorage.setItem(key, value);
            return;
        }
    }
    catch {
        // Fall through to in-memory persistence when localStorage is unavailable.
    }
    InMemoryStorage.set(key, value);
};
const ReadFromStorage = (path, initial) => {
    try {
        const stored = ReadStoredString(`${STORAGE_KEY_ROOT}/${path}`);
        return stored ? JSON.parse(stored) : initial;
    }
    catch {
        return initial;
    }
};
const WriteToStorage = (path, data) => {
    WriteStoredString(`${STORAGE_KEY_ROOT}/${path}`, JSON.stringify(data));
};
// ============================================================================
// Reducer
// ============================================================================
const AccordionReducer = (state, action) => {
    switch (action.type) {
        case "SET_SEARCH_TERM":
            return { ...state, searchTerm: action.term };
        case "SET_EDIT_MODE":
            return { ...state, editMode: action.enabled };
        case "TOGGLE_PINNED": {
            const isPinned = state.pinnedIds.includes(action.itemId);
            return {
                ...state,
                pinnedIds: isPinned ? state.pinnedIds.filter((id) => id !== action.itemId) : [...state.pinnedIds, action.itemId],
            };
        }
        case "TOGGLE_HIDDEN": {
            const isHidden = state.hiddenIds.includes(action.itemId);
            return {
                ...state,
                hiddenIds: isHidden ? state.hiddenIds.filter((id) => id !== action.itemId) : [...state.hiddenIds, action.itemId],
            };
        }
        case "MOVE_PINNED_UP": {
            const index = state.pinnedIds.indexOf(action.itemId);
            if (index <= 0) {
                return state;
            }
            const newPinnedIds = [...state.pinnedIds];
            [newPinnedIds[index - 1], newPinnedIds[index]] = [newPinnedIds[index], newPinnedIds[index - 1]];
            return { ...state, pinnedIds: newPinnedIds };
        }
        case "REMOVE_STALE_IDS": {
            const pinnedIds = state.pinnedIds.filter((id) => action.activeIds.has(id));
            const hiddenIds = state.hiddenIds.filter((id) => action.activeIds.has(id));
            if (pinnedIds.length === state.pinnedIds.length && hiddenIds.length === state.hiddenIds.length) {
                return state;
            }
            return { ...state, pinnedIds, hiddenIds };
        }
        case "SHOW_ALL":
            return { ...state, hiddenIds: [] };
        case "HIDE_ALL_VISIBLE":
            return {
                ...state,
                hiddenIds: [...new Set([...state.hiddenIds, ...action.visibleItemIds])],
            };
        default:
            return state;
    }
};
export const AccordionContext = createContext(undefined);
/**
 * Hook to create and manage the AccordionContext value.
 *
 * @param props - AccordionProps
 * @returns AccordionContextValue, or undefined if no features are enabled or no uniqueId is provided.
 */
export function useAccordionContext(props) {
    const { uniqueId: accordionId, enablePinnedItems, enableHiddenItems, enableSearchItems } = props;
    const features = useMemo(() => ({
        pinning: enablePinnedItems ?? false,
        hiding: enableHiddenItems ?? false,
        search: enableSearchItems ?? false,
    }), [enablePinnedItems, enableHiddenItems, enableSearchItems]);
    const hasFeatures = features.pinning || features.hiding || features.search;
    // Initialize state from localStorage
    const initialState = useMemo(() => {
        if (!accordionId || !hasFeatures) {
            return { pinnedIds: [], hiddenIds: [], searchTerm: "", editMode: false };
        }
        return {
            pinnedIds: features.pinning ? ReadFromStorage(`Pinned/${accordionId}`, []) : [],
            hiddenIds: features.hiding ? ReadFromStorage(`Hidden/${accordionId}`, []) : [],
            searchTerm: "",
            editMode: false,
        };
    }, [accordionId, hasFeatures, features.pinning, features.hiding]);
    const [state, dispatch] = useReducer(AccordionReducer, initialState);
    const pinnedContainerRef = useRef(null);
    const registeredItemIds = useRef(new Map());
    // Persist pinnedIds to localStorage when they change
    useEffect(() => {
        if (accordionId && features.pinning) {
            WriteToStorage(`Pinned/${accordionId}`, state.pinnedIds);
        }
    }, [accordionId, features.pinning, state.pinnedIds]);
    // Persist hiddenIds to localStorage when they change
    useEffect(() => {
        if (accordionId && features.hiding) {
            WriteToStorage(`Hidden/${accordionId}`, state.hiddenIds);
        }
    }, [accordionId, features.hiding, state.hiddenIds]);
    // Clean stale IDs from localStorage on mount. Parent effects run after
    // children's, so all items will have registered by the time this fires.
    useEffect(() => {
        if (accordionId && hasFeatures) {
            dispatch({ type: "REMOVE_STALE_IDS", activeIds: new Set(registeredItemIds.current.keys()) });
        }
    }, [accordionId, hasFeatures]);
    // Return undefined if no accordionId or no features enabled
    if (!accordionId || !hasFeatures) {
        return undefined;
    }
    return {
        accordionId,
        state,
        dispatch,
        features,
        pinnedContainerRef,
        registeredItemIds: registeredItemIds.current,
    };
}
export const AccordionSectionBlockContext = createContext(undefined);
/**
 * Hook to create the AccordionSectionBlockContext value.
 *
 * @param props - AccordionSectionBlockProps
 * @returns AccordionSectionBlockContextValue and isEmpty state
 */
export function useAccordionSectionBlockContext(props) {
    const { sectionId } = props;
    const context = useMemo(() => ({ sectionId }), [sectionId]);
    const isEmpty = useIsSectionEmpty(sectionId);
    return { context, isEmpty };
}
// ============================================================================
// Item Depth Context (to detect nested AccordionSectionItems)
// ============================================================================
/**
 * Context to track whether we're inside an AccordionSectionItem.
 * Used to prevent nested items from being individually manageable.
 */
export const AccordionItemDepthContext = createContext(false);
/**
 * Hook to compute item state from accordion context.
 *
 * @param props - AccordionSectionItemProps
 * @returns AccordionItemState, or undefined if no accordion context or nested item.
 */
export function useAccordionSectionItemState(props) {
    const { uniqueId: itemId, label: itemLabel, staticItem } = props;
    const accordionCtx = useContext(AccordionContext);
    const sectionCtx = useContext(AccordionSectionBlockContext);
    const isNested = useContext(AccordionItemDepthContext);
    // Build the globally unique item ID
    const itemUniqueId = useMemo(() => {
        if (!accordionCtx || !sectionCtx) {
            return "";
        }
        return `${accordionCtx.accordionId}/${sectionCtx.sectionId}/${itemId}`;
    }, [accordionCtx?.accordionId, sectionCtx?.sectionId, itemId]);
    // Debug: warn if itemId changes (should be stable)
    const prevItemIdRef = useRef(itemId);
    useEffect(() => {
        if (accordionCtx && prevItemIdRef.current !== itemId) {
            Warn(`Accordion: The uniqueId "${itemId}" in section "${sectionCtx?.sectionId}" has changed from "${prevItemIdRef.current}". ` +
                `Each item must have a unique, stable ID for pin/hide persistence to work correctly.`);
        }
        prevItemIdRef.current = itemId;
    }, [accordionCtx, itemId, sectionCtx?.sectionId]);
    // Register item and detect duplicates (skip nested items, as children of other AccordionSectionItem should not participate in pin/hide/search).
    useEffect(() => {
        if (!accordionCtx || !itemUniqueId || isNested) {
            return;
        }
        const { registeredItemIds } = accordionCtx;
        if (registeredItemIds.has(itemUniqueId)) {
            Warn(`Accordion: Duplicate uniqueId "${itemId}" detected in section "${sectionCtx?.sectionId}". ` +
                `Each item must have a unique ID within its section for pin/hide persistence to work correctly.`);
        }
        registeredItemIds.set(itemUniqueId, itemLabel ?? itemId);
        return () => {
            registeredItemIds.delete(itemUniqueId);
        };
    }, [accordionCtx, itemUniqueId, itemId, itemLabel, sectionCtx?.sectionId, isNested]);
    // If no context, static item, or nested, return undefined
    if (!accordionCtx || staticItem) {
        return undefined;
    }
    const { state, dispatch, features } = accordionCtx;
    const { pinnedIds, hiddenIds, searchTerm, editMode } = state;
    // Compute derived state
    const isPinned = features.pinning && pinnedIds.includes(itemUniqueId);
    const isHidden = features.hiding && hiddenIds.includes(itemUniqueId);
    const pinnedIndex = isPinned ? pinnedIds.indexOf(itemUniqueId) : -1;
    const canMoveUp = isPinned && pinnedIndex > 0;
    // Search matching
    const searchText = (itemLabel ?? itemId).toLowerCase();
    const isMatch = !features.search || !searchTerm || searchText.includes(searchTerm.toLowerCase());
    return {
        itemUniqueId,
        isNested,
        isPinned,
        isHidden,
        isMatch,
        pinnedIndex,
        canMoveUp,
        inEditMode: editMode,
        actions: {
            togglePinned: () => dispatch({ type: "TOGGLE_PINNED", itemId: itemUniqueId }),
            toggleHidden: () => dispatch({ type: "TOGGLE_HIDDEN", itemId: itemUniqueId }),
            movePinnedUp: () => dispatch({ type: "MOVE_PINNED_UP", itemId: itemUniqueId }),
        },
    };
}
/**
 * Hook to determine if a section is empty (has no visible items).
 * Derived from accordion state + globally registered items filtered by section prefix.
 *
 * @param sectionId - The section ID to check.
 * @returns Whether the section has no visible items.
 */
function useIsSectionEmpty(sectionId) {
    const accordionCtx = useContext(AccordionContext);
    if (!accordionCtx) {
        return false;
    }
    const { state, features, accordionId, registeredItemIds } = accordionCtx;
    const { pinnedIds, hiddenIds, searchTerm, editMode } = state;
    if (editMode) {
        return false;
    }
    const sectionPrefix = `${accordionId}/${sectionId}/`;
    let hasItems = false;
    for (const [itemId, itemLabel] of registeredItemIds) {
        if (!itemId.startsWith(sectionPrefix)) {
            continue;
        }
        hasItems = true;
        const isPinned = features.pinning && pinnedIds.includes(itemId);
        const isHidden = features.hiding && hiddenIds.includes(itemId);
        const searchText = (itemLabel || itemId).toLowerCase();
        const isMatch = !features.search || !searchTerm || searchText.includes(searchTerm.toLowerCase());
        if (!isPinned && !isHidden && isMatch) {
            return false;
        }
    }
    return hasItems;
}
//# sourceMappingURL=accordion.contexts.js.map